import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, AlertTriangle, FilePlus, Minus, Plus } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// AmendmentDialog — add a Tx Plan amendment.
// =====================================================================
// Spec: Cameron 2026-05-07. Tx Plan is the single source of truth for
// billing. Treatment-related changes (procedure ran long, additional
// materials, complications, mid-treatment discounts) flow as new
// is_amendment=true line items via dos_v2.add_plan_amendment.
//
// Out of scope (DO NOT build): a generic "off-plan charge" path. This
// dialog is the ONLY UI affordance for changing what an Accepted
// plan bills for treatment-related reasons. Retail/admin/sterilization
// fees ship as a separate billing module.
//
// The seven reason codes below map 1:1 to the database enum.
// `other` is the only one that requires the notes field.
// =====================================================================

const REASON_OPTIONS = [
  {
    code: "extended_chair_time",
    label: "Extended chair time",
    blurb: "Procedure took longer than the planned schedule.",
  },
  {
    code: "additional_materials",
    label: "Additional materials",
    blurb: "Materials beyond the original plan (e.g. extra crown unit).",
  },
  {
    code: "complication_or_additional_procedure",
    label: "Complication / additional procedure",
    blurb: "Unplanned procedure performed during the visit.",
  },
  {
    code: "patient_requested_change",
    label: "Patient-requested change",
    blurb: "Patient asked for a scope change during treatment.",
  },
  {
    code: "pricing_correction",
    label: "Pricing correction or missed item",
    blurb: "Price was wrong on the original plan, or a procedure should have been on it from the start and was missed. Use this for honest oversights.",
  },
  {
    code: "mid_treatment_discount",
    label: "Mid-treatment discount",
    blurb: "Goodwill discount applied during treatment (use a negative amount).",
  },
  {
    code: "other",
    label: "Other (notes required)",
    blurb: "Anything not covered above. Notes mandatory.",
  },
];

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  const pesos = Math.round(centavos / 100);
  const sign = pesos < 0 ? "-" : "";
  return `${sign}₱${Math.abs(pesos).toLocaleString()}`;
}

export default function AmendmentDialog({
  open,
  onOpenChange,
  planId,
  planCode,
  patientName,
  parentLineItem = null,   // optional: pre-set parent_line_item_id (when amending a specific original)
  onSaved,                 // (result) => void — fired after add_plan_amendment succeeds
}) {
  const [reasonCode, setReasonCode] = useState("extended_chair_time");
  const [description, setDescription] = useState("");
  const [amountPesos, setAmountPesos] = useState("");
  const [isNegative, setIsNegative] = useState(false);   // sign toggle for discounts/refunds
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReasonCode(parentLineItem ? "pricing_correction" : "extended_chair_time");
    setDescription(parentLineItem
      ? `${parentLineItem.display_label || parentLineItem.procedure_name || "Procedure"} — adjustment`
      : "");
    setAmountPesos("");
    setIsNegative(false);
    setNotes("");
    setSubmitting(false);
  }, [open, parentLineItem]);

  // Discount preset auto-flips to negative for clarity. The user can
  // still manually toggle if they meant something else.
  useEffect(() => {
    if (reasonCode === "mid_treatment_discount") setIsNegative(true);
  }, [reasonCode]);

  if (!open) return null;

  const reason = REASON_OPTIONS.find((r) => r.code === reasonCode);
  const notesRequired = reasonCode === "other";
  const pesos = Number(amountPesos);
  const validAmount = Number.isFinite(pesos) && pesos > 0;
  const validDescription = description.trim().length > 0;
  const validNotes = !notesRequired || notes.trim().length > 0;
  const canSubmit = !submitting && validAmount && validDescription && validNotes;

  const amountCentavos = validAmount
    ? (isNegative ? -1 : 1) * Math.round(pesos * 100)
    : 0;

  const close = () => { if (!submitting) onOpenChange(false); };

  async function save() {
    if (!canSubmit) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("add_plan_amendment", {
      p_plan_id:             planId,
      p_reason_code:         reasonCode,
      p_description:         description.trim(),
      p_amount_centavos:     amountCentavos,
      p_notes:               notes.trim() || null,
      p_parent_line_item_id: parentLineItem?.id ?? null,
      p_procedure_id:        parentLineItem?.procedure_id ?? null,
      p_tooth_number:        parentLineItem?.tooth_number ?? null,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Couldn't add amendment.");
      return;
    }
    toast.success(amountCentavos < 0
      ? "Amendment recorded. Plan total decreased."
      : "Amendment recorded. Plan total increased.");
    onSaved?.(data);
    onOpenChange(false);
  }

  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="amendment-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FilePlus className="w-4 h-4 text-teal-600" strokeWidth={2.25} />
              Add plan amendment
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {patientName ? <span className="text-slate-700 font-medium">{patientName}</span> : null}
              {planCode ? <> · <span className="font-mono">{planCode}</span></> : null}
              {parentLineItem ? <> · amending <strong>{parentLineItem.display_label || parentLineItem.procedure_name}</strong></> : null}
            </p>
          </div>
          <button type="button" onClick={close} disabled={submitting}
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {/* Soft contextual intro — explains why this dialog exists so
              the doctor doesn't read amendments as punishment for the
              honest "oh, I forgot to include this" cases. */}
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700 leading-snug">
            The plan is already accepted, so anything that changes what's billed —
            even an honest "I forgot to include this" — flows through here so the
            patient sees a clear before/after and the audit trail stays clean.
            Pick the reason that fits closest; <strong>Pricing correction or missed item</strong> covers oversights.
          </div>

          {/* Reason picker */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="amendment-reason">
              Reason
            </label>
            <select
              id="amendment-reason"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              data-testid="amendment-reason"
              className="mt-1 w-full px-2 py-2 text-sm rounded border border-slate-300 focus:border-teal-500 outline-none bg-white"
            >
              {REASON_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>{opt.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">{reason?.blurb}</p>
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="amendment-desc">
              Description
            </label>
            <input
              id="amendment-desc"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Surgical Extraction — extra chair time"
              data-testid="amendment-desc"
              className="mt-1 w-full px-2 py-1.5 text-sm rounded border border-slate-300 focus:border-teal-500 outline-none"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Shows on the receipt and in the audit log. Keep it short and clear.
            </p>
          </div>

          {/* Amount + sign */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="amendment-amount">
              Amount
            </label>
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsNegative(!isNegative)}
                data-testid="amendment-sign-toggle"
                title={isNegative ? "Currently a discount/refund. Click to bill." : "Currently a charge. Click for discount/refund."}
                className={[
                  "inline-flex items-center justify-center w-8 h-8 rounded border text-sm font-semibold",
                  isNegative
                    ? "border-rose-300 bg-rose-50 text-rose-700"
                    : "border-slate-300 bg-white text-slate-700",
                ].join(" ")}
              >
                {isNegative ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </button>
              <span className="text-slate-500">₱</span>
              <input
                id="amendment-amount"
                type="number"
                min={0}
                step={1}
                value={amountPesos}
                onChange={(e) => setAmountPesos(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="0"
                data-testid="amendment-amount"
                className="flex-1 px-2 py-1.5 text-sm rounded border border-slate-300 focus:border-teal-500 outline-none tabular-nums"
              />
              <span className="text-[12px] tabular-nums text-slate-700">
                {validAmount ? <strong className={isNegative ? "text-rose-700" : "text-slate-900"}>{formatPHP(amountCentavos)}</strong> : null}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Use the <Plus className="w-3 h-3 inline" /> button for charges (most amendments).
              Toggle to <Minus className="w-3 h-3 inline" /> for discounts or refunds — the plan total drops by that amount.
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="amendment-notes">
              Notes {notesRequired ? <span className="text-rose-600">*required</span> : <span className="text-slate-400">(optional)</span>}
            </label>
            <textarea
              id="amendment-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={notesRequired
                ? "Required — explain why this amendment doesn't fit the standard reasons."
                : "Optional — extra context for the audit log."}
              data-testid="amendment-notes"
              className="mt-1 w-full px-3 py-2 text-sm rounded border border-slate-300 focus:border-teal-500 outline-none resize-none"
            />
          </div>

          {!validAmount && amountPesos !== "" ? (
            <div className="text-[11px] text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Enter a positive number; use the sign toggle for discounts.
            </div>
          ) : null}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3 shrink-0">
          <button type="button" onClick={close} disabled={submitting}
            className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded">
            Cancel
          </button>
          <button type="button"
            onClick={save}
            disabled={!canSubmit}
            data-testid="amendment-save"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:bg-slate-300 disabled:cursor-not-allowed">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus className="w-3.5 h-3.5" />}
            Save amendment
          </button>
        </div>
      </div>
    </div>
  );
}
